import { useState, useCallback, useRef } from 'react';

interface UseBottomSheetOptions {
  onDismiss: () => void;
  defaultHeightPercent?: number;
  maxHeightPercent?: number;
  dismissThreshold?: number;
}

export function useBottomSheet({
  onDismiss,
  defaultHeightPercent = 70,
  maxHeightPercent = 90,
  dismissThreshold = 0.3,
}: UseBottomSheetOptions) {
  const [heightPercent, setHeightPercent] = useState(defaultHeightPercent);
  const [translateY, setTranslateY] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const dragStart = useRef<{ startY: number; startHeight: number } | null>(null);

  const onDragHandleDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragStart.current = { startY: e.clientY, startHeight: heightPercent };
    setIsAnimating(false);
  }, [heightPercent]);

  const onDragHandleMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dy = e.clientY - dragStart.current.startY;
    const vh = window.innerHeight;

    if (dy > 0) {
      setTranslateY(dy);
    } else {
      const newPercent = Math.min(
        maxHeightPercent,
        dragStart.current.startHeight + (-dy / vh) * 100,
      );
      setHeightPercent(newPercent);
      setTranslateY(0);
    }
  }, [maxHeightPercent]);

  const onDragHandleUp = useCallback(() => {
    if (!dragStart.current) return;
    setIsAnimating(true);

    const vh = window.innerHeight;
    const sheetHeight = (heightPercent / 100) * vh;

    if (translateY > sheetHeight * dismissThreshold) {
      setTranslateY(vh);
      setTimeout(onDismiss, 200);
    } else {
      setTranslateY(0);
    }

    dragStart.current = null;
  }, [heightPercent, translateY, dismissThreshold, onDismiss]);

  return {
    heightPercent,
    translateY,
    isAnimating,
    dragHandleProps: {
      onPointerDown: onDragHandleDown,
      onPointerMove: onDragHandleMove,
      onPointerUp: onDragHandleUp,
    },
    sheetStyle: {
      height: `${heightPercent}vh`,
      transform: `translateY(${translateY}px)`,
      transition: isAnimating ? 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1), height 0.2s ease' : 'none',
    } as React.CSSProperties,
  };
}
